import { useCallback, useEffect, useMemo, useState } from "react";

import { IVehicle } from "@/types/vehicle.type";
import { getIn, useFormikContext } from "formik";

import SelectFormik from "@/common/components/form/SelectFormik";
import ButtonWithLoader from "@/common/components/ui/loading/ButtonLoading";
import { CheckboxField } from "@/modules/renters/components/modals/fields/EditBondRenterVehcile/formsModal/field/CheckboxField";
import RegisterPhotos from "@/modules/renters/components/modals/fields/EditBondRenterVehcile/formsModal/field/RegisterPhotos";
import { partsEvaluationData } from "@/common/helpers/partsEvaluationData";
import InputFormik from "@/common/components/form/InputFormik";
import { IRenter } from "@/types/renter.type";
import { getRenterByDocument, getVehicleList, sendRenterSelfieWithAddress } from "@/common/services/pre-collection.api";
import { HiSearch } from "react-icons/hi";
import GenericInput from "@/common/components/form/GenericInput";
import { UploadDocumentField } from "@/modules/renters/components/forms/fields/UploadDocumentField";
import { UploadImageField } from "@/common/components/form/UploadImageField/UploadImageField";
import { useToastContext } from "@/common/components/ui/toast/toast.context";

type CollectorLocation = {
  mapsUrl: string;
  latitude: number;
  longitude: number;
  currentTime: Date;
}

type InspectionFormValues = {
  vehicleId: string;
  renterId: string;
  selectedLicensePlate: string;
  selfieWithAddress: string;
  registers: Array<{ type: string; content: string; verified?: boolean }>;
  optionRepossesion: string;
};

type Props = {
  submitButtonText: string;
  isLoading: boolean;
};

export default function VehicleInspectionFormBody({ submitButtonText, isLoading }: Props) {
  const { notifyInfo, notifyError } = useToastContext();

  const formik = useFormikContext<InspectionFormValues>();
  const { values, setFieldValue } = formik;

  const registers = getIn(formik.values, "registers");
  const optionRepossesion = getIn(formik.values, "optionRepossesion");

  const [renter, setRenter] = useState<IRenter | null>(null);
  const [vehicles, setVehicles] = useState<IVehicle[]>([]);
  const [disableButton, setDisableButton] = useState(true);
  const [collectorLocation, setCollectorLocation] = useState<CollectorLocation | null>(null);
  const [selfieWithAddressSented, setSelfieWithAddressSent] = useState(false);

  const pathToUpload = useMemo(() => `pre-collection/${renter?._id}`, [renter?._id]);

  useEffect(() => {
    if (optionRepossesion !== "") {
      setDisableButton(false);
    } else {
      setDisableButton(true);
    }
  }, [optionRepossesion]);


  const onAddRegister = () => {
    formik.setFieldValue("registers", [
      ...registers,
      { type: "", content: "", verified: false },
    ]);
  };

  const handleVehicleChange = useCallback((e: React.ChangeEvent<HTMLSelectElement> | { target: { value: string; } }) => {
    const selectedPlate = e.target.value;
    const currentPlate = values.selectedLicensePlate;

    if (!selectedPlate || selectedPlate === "") {
      setFieldValue("selectedLicensePlate", "");
      setFieldValue("vehicleId", "");
      setFieldValue("registers", []);
      return;
    }

    if (selectedPlate !== currentPlate) {
      setFieldValue("selectedLicensePlate", selectedPlate);

      const selectedVehicle = vehicles.find(
        (v) => v.licensePlate === selectedPlate
      );

      if (selectedVehicle) {
        setFieldValue("vehicleId", selectedVehicle._id);

        // Reset dos registros
        const initialRegisters = [
          { type: "Frente do veículo", content: "" },
          { type: "Lateral direita do veículo", content: "" },
          { type: "Lateral esquerda do veículo", content: "" },
          { type: "Traseira do veículo", content: "" },
          { type: "Odômetro", content: "" },
        ];
        setFieldValue("registers", initialRegisters);
      }
    }
  }, [values.selectedLicensePlate, vehicles, setFieldValue]);


  const handleGetRenter = useCallback(async (renderDocument: string) => {
    if (renderDocument.length < 14) return

    try {
      if (isLoading) {
        notifyInfo("Aguarde o término do carregamento atual.");
        return;
      }

      const response = await getRenterByDocument(renderDocument);

      if (response.ok && response.data) {
        setRenter(response.data);
        setFieldValue("renterId", response.data._id);

        const vehicleListResponse = await getVehicleList()

        const vehiclesFilteredByCompany = vehicleListResponse.data.filter(
          vehicle => vehicle.companyId === response.data.companyId
        )

        setVehicles(vehiclesFilteredByCompany);
      } else {
        setRenter(null);
        setFieldValue("renterId", "");
      }
    } catch (error) {
      console.error("Erro ao buscar cliente:", error);
      setRenter(null);
      setFieldValue("renterId", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRenterSelfieUpload = useCallback(async (src: string) => {
    try {
      if (!values.renterId) return;

      if (!collectorLocation) {
        notifyError("Localização do coletor não disponível. Não é possível enviar a selfie com endereço.");
        return;
      }

      setFieldValue("selfieWithAddress", src);
      setSelfieWithAddressSent(true);

      const response = await sendRenterSelfieWithAddress(values.renterId, {
        vehicleId: values.vehicleId,
        licensePlate: values.selectedLicensePlate,

        collector: {
          name: renter?.name || "",
          document: renter?.cpf || "",
          selfieWithAddress: src,
          location: collectorLocation
        }
      });

      if (response.ok) {
        setSelfieWithAddressSent(true);
      } else {
        setSelfieWithAddressSent(false);
        setFieldValue("selfieWithAddress", "");
        const { message } = response.data

        notifyInfo(message || "Falha ao enviar selfie com endereço. Tente novamente.");
      }
    } catch (error) {
      console.error("Erro ao enviar selfie com endereço:", error);
      setSelfieWithAddressSent(false);
      setFieldValue("selfieWithAddress", "");
    }


    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifyInfo, renter, setFieldValue, values.renterId, values.selectedLicensePlate, values.vehicleId]);

  const registerPhotosComponent = useMemo(() => {
    // Se não há placa selecionada, retorna versão desabilitada
    if (!values.selectedLicensePlate && !selfieWithAddressSented) {
      return (
        <div className="opacity-50 pointer-events-none">
          <RegisterPhotos
            key="empty" // Key diferente para forçar remount
            title={"Inspeção de Veículo"}
            items={[]}
            onAddItem={onAddRegister}
            onRemoveItem={onAddRegister}
            titleTextArea={"Descreva o motivo da devolução:"}
            renterId={""}
            nameRenter={""}
            vehicleId={""}
          />
        </div>
      );
    }

    // Componente ativo com dados da placa selecionada
    return (
      <RegisterPhotos
        key={values.selectedLicensePlate} // Key baseada na placa para forçar remount
        title={"Inspeção de Veículo"}
        items={registers}
        onAddItem={onAddRegister}
        onRemoveItem={onAddRegister}
        titleTextArea={"Descreva o motivo da devolução:"}
        renterId={""}
        nameRenter={""}
        vehicleId={values.vehicleId}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.selectedLicensePlate, values.vehicleId, selfieWithAddressSented]);

  const handleGetCollectorLocation = useCallback(() => {
    if (collectorLocation) return;

    if (confirm("Permitir que o sistema acesse sua localização para registrar o local do recolhimento?")) {
      if (!navigator.geolocation) {
        notifyInfo("Geolocalização não é suportada pelo seu navegador.");
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords

          const mapsUrl = `https://www.google.com/maps?q=${latitude},${longitude}`;


          setCollectorLocation({ mapsUrl, latitude, longitude, currentTime: new Date() });
        },
        (error) => {
          switch (error.code) {
            case error.PERMISSION_DENIED:
              notifyError("Permissão negada para acessar a localização.");
              break;
            case error.POSITION_UNAVAILABLE:
              notifyError("Informações de localização indisponíveis.");
              break;
            case error.TIMEOUT:
              notifyError("Tempo esgotado ao tentar obter localização.");
              break;
            default:
              notifyError("Erro desconhecido ao obter localização.");
              break;
          }
        }
      )

      return
    }

    notifyError("Não é possível realizar o recolhimento sem informar a sua localização.");
  }, [collectorLocation, notifyError, notifyInfo])

  useEffect(() => { handleGetCollectorLocation(); }, [handleGetCollectorLocation])

  return (
    <section>
      <div className="w-full flex items-end gap-3 mb-4">
        <InputFormik
          name="renterId"
          label="Informe o CPF:"
          placeholder="Informe seu CPF correto"
          mask={(value) => {
            const digits = (value ?? "").replace(/\D/g, "").slice(0, 11);

            if (digits.length <= 3) return digits;
            if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
            if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;

            return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
          }}
          className="w-full"
        />

        <ButtonWithLoader type="button" onClick={() => handleGetRenter(values.renterId)} >
          <HiSearch className="search-icon" />
        </ButtonWithLoader>
      </div>

      {renter && (
        <>
          <div className="mb-4">
            <GenericInput
              label={"Nome do recolhedor:"}
              value={renter.name}
              mask={(value: string) => value}
            />
          </div>
        </>
      )}

      {renter && renter._id && (
        <div className="mb-4">
          <SelectFormik
            label="Placa do Veículo:"
            name="selectedLicensePlate"
            options={vehicles.map(vehicle => vehicle.licensePlate)}
            onChange={handleVehicleChange}
            allowFilter={true}
            placeholder="Digite a placa para filtrar..."
          />
        </div>
      )}

      {values.selectedLicensePlate && values.renterId && (
        <div className="mb-4">
          <label className="font-semibold text-sm">Selfie com Endereço:</label>

          <UploadDocumentField
            currentSrc={values.selfieWithAddress}
            pathToUpload={pathToUpload}
            handleUploadSrc={handleRenterSelfieUpload}
          >
            {({ src, onNewFile, onRemoveClick }) => (
              <UploadImageField
                src={src}
                onNewFile={onNewFile}
                onRemoveClick={onRemoveClick}
              />
            )}
          </UploadDocumentField>
        </div>
      )}




      {registerPhotosComponent}
      <div className="mt-8">
        <h3 className="text-xl font-semibold mb-4">Avaliação das Peças</h3>
        {partsEvaluationData.map((part) => (
          <CheckboxField
            key={part.name}
            name={`partsEvaluation.${part.name}`}
            label={part.label}
            onChange={() => { }}
          />
        ))}

        <div className="flex justify-end mt-2">
          <ButtonWithLoader
            disabled={disableButton}
            type="submit"
            isLoading={isLoading}
          >
            {submitButtonText}
          </ButtonWithLoader>
        </div>
      </div>
    </section>
  );
}
